import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';
import * as XLSX from 'xlsx';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'active' ? 'badge badge-success' : s === 'inactive' ? 'badge badge-danger' : s === 'on leave' ? 'badge badge-warning' : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function EmployeeTable({ onEdit }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState({ fullName: '', designation: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const menuRef = useRef();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchEmployees = async () => {
    try {
      const data = await fetchWithCorpId(`/employees?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setEmployees(data); setFilteredEmployees(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setEmployees(content); setFilteredEmployees(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to fetch employees'); } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await fetchWithCorpId(`/employees/${id}`, { method: 'DELETE' });
      await fetchEmployees(); setMessage('Employee deleted successfully.');
    } catch { setError('Failed to delete employee'); }
  };

  useEffect(() => {
    fetchEmployees();
    const close = () => {};
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, employees]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const applyFilters = () => {
    setFilteredEmployees(employees.filter(e =>
      (!filter.fullName || e.fullName?.toLowerCase().includes(filter.fullName.toLowerCase())) &&
      (!filter.designation || e.designation?.toLowerCase().includes(filter.designation.toLowerCase())) &&
      (!filter.status || e.status?.toLowerCase() === filter.status.toLowerCase())
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key) return;
    setFilteredEmployees(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredEmployees.map(e => ({
      'Employee ID': e.id, Name: e.fullName, Designation: e.designation,
      Status: e.status, Category: e.category, 'Work Auth': e.workAuth,
      'Start Date': e.startDate, 'End Date': e.endDate,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'employees.xlsx');
  };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Employees</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Manage employee records and assignments</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-info" onClick={handleDownloadExcel}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn-primary" onClick={() => navigate('/on/employees/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Employee
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input name="fullName" placeholder="Name" value={filter.fullName} onChange={handleFilterChange} />
        <input name="designation" placeholder="Designation" value={filter.designation} onChange={handleFilterChange} />
        <select name="status" value={filter.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="On Leave">On Leave</option>
        </select>
        <button className="btn-secondary" onClick={() => setFilter({ fullName: '', designation: '', status: '' })}>Clear</button>
      </div>

      {message && <div className="alert success" style={{ marginBottom: '14px' }}>{message}</div>}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading employees…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>ID <SortIcon col="id" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('fullName')}>Name <SortIcon col="fullName" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('designation')}>Designation <SortIcon col="designation" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>Category <SortIcon col="category" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('workAuth')}>Work Auth <SortIcon col="workAuth" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startDate')}>Start Date <SortIcon col="startDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('endDate')}>End Date <SortIcon col="endDate" sortConfig={sortConfig} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr className="state-row"><td colSpan="8">No employees found.</td></tr>
              ) : filteredEmployees.map(emp => (
                <tr key={emp.id}>
                  <td><span className="table-link" onClick={() => onEdit(emp)}>{emp.id}</span></td>
                  <td style={{ fontWeight: '500', color: '#1e293b' }}>{emp.fullName}</td>
                  <td style={{ color: '#64748b' }}>{emp.designation}</td>
                  <td><StatusBadge status={emp.status} /></td>
                  <td>{emp.category}</td>
                  <td>{emp.workAuth}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{emp.startDate}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{emp.endDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeTable;
